import React, { useState, useEffect } from 'react';
import { MapPin, Briefcase, GraduationCap, IndianRupee } from 'lucide-react';
import { RegistrationForm } from '../../types';
import { DROPDOWN_OPTIONS } from '../../utils/constants';
import apiService from '../../services/api';

interface ProfileDetailsStepProps {
  formData: RegistrationForm;
  updateFormData: (data: any) => void;
}

const ProfileDetailsStep: React.FC<ProfileDetailsStepProps> = ({ formData, updateFormData }) => {
  const [localData, setLocalData] = useState(formData.profileDetails);
  const [cities, setCities] = useState<{ value: string; label: string }[]>([]);
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    updateFormData({ profileDetails: localData });
  }, [localData, updateFormData]);

  useEffect(() => {
    if (localData.workLocation.type === 'india' && localData.workLocation.state) {
      loadCities(localData.workLocation.state);
    }
  }, [localData.workLocation.state, localData.workLocation.type]);

  const loadCities = async (state: string) => {
    setLoadingCities(true);
    try {
      const response = await apiService.getCities(state);
      setCities(response.cities || []);
    } catch (error) {
      console.error('Failed to load cities:', error);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('workLocation.')) {
      const field = name.split('.')[1];
      setLocalData(prev => ({
        ...prev,
        workLocation: {
          ...prev.workLocation,
          [field]: value
        }
      }));
    } else {
      setLocalData(prev => ({ ...prev, [name]: value }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Caste Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="caste" className="block text-sm font-medium text-gray-700 mb-1">
            Caste *
          </label>
          <select
            id="caste"
            name="caste"
            value={localData.caste}
            onChange={handleChange}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="reddy">Reddy</option>
            <option value="others">Others</option>
          </select>
        </div>

        <div>
          <label htmlFor="subcaste" className="block text-sm font-medium text-gray-700 mb-1">
            Sub-caste
          </label>
          <input
            type="text"
            id="subcaste"
            name="subcaste"
            value={localData.subcaste}
            onChange={handleChange}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter sub-caste (optional)"
          />
        </div>

        <div>
          <label htmlFor="gothra" className="block text-sm font-medium text-gray-700 mb-1">
            Gothra *
          </label>
          <input
            type="text"
            id="gothra"
            name="gothra"
            value={localData.gothra}
            onChange={handleChange}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter your gothra"
            required
          />
        </div>
      </div>

      {/* Personal Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="maritalStatus" className="block text-sm font-medium text-gray-700 mb-1">
            Marital Status *
          </label>
          <select
            id="maritalStatus"
            name="maritalStatus"
            value={localData.maritalStatus}
            onChange={handleChange}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {DROPDOWN_OPTIONS.maritalStatus.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="height" className="block text-sm font-medium text-gray-700 mb-1">
            Height *
          </label>
          <select
            id="height"
            name="height"
            value={localData.height}
            onChange={handleChange}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          >
            <option value="">Select height</option>
            {DROPDOWN_OPTIONS.height.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="complexion" className="block text-sm font-medium text-gray-700 mb-1">
            Complexion *
          </label>
          <select
            id="complexion"
            name="complexion"
            value={localData.complexion}
            onChange={handleChange}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {DROPDOWN_OPTIONS.complexion.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Professional Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label htmlFor="education" className="block text-sm font-medium text-gray-700 mb-1">
            Education *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <GraduationCap className="h-5 w-5 text-gray-400" />
            </div>
            <select
              id="education"
              name="education"
              value={localData.education}
              onChange={handleChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">Select education</option>
              {DROPDOWN_OPTIONS.education.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="occupation" className="block text-sm font-medium text-gray-700 mb-1">
            Occupation *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Briefcase className="h-5 w-5 text-gray-400" />
            </div>
            <select
              id="occupation"
              name="occupation"
              value={localData.occupation}
              onChange={handleChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">Select occupation</option>
              {DROPDOWN_OPTIONS.occupation.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="annualIncome" className="block text-sm font-medium text-gray-700 mb-1">
            Annual Income *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IndianRupee className="h-5 w-5 text-gray-400" />
            </div>
            <select
              id="annualIncome"
              name="annualIncome"
              value={localData.annualIncome}
              onChange={handleChange}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">Select income range</option>
              {DROPDOWN_OPTIONS.annualIncome.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Work Location */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Work Location</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Location Type *
          </label>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="workLocation.type"
                value="india"
                checked={localData.workLocation.type === 'india'}
                onChange={handleChange}
                className="mr-2"
              />
              India
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="workLocation.type"
                value="nri"
                checked={localData.workLocation.type === 'nri'}
                onChange={handleChange}
                className="mr-2"
              />
              NRI
            </label>
          </div>
        </div>

        {localData.workLocation.type === 'india' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="workLocation.state" className="block text-sm font-medium text-gray-700 mb-1">
                State *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  name="workLocation.state"
                  value={localData.workLocation.state}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  required
                >
                  <option value="">Select state</option>
                  {DROPDOWN_OPTIONS.indianStates.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="workLocation.city" className="block text-sm font-medium text-gray-700 mb-1">
                City *
              </label>
              <select
                name="workLocation.city"
                value={localData.workLocation.city}
                onChange={handleChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
                disabled={!localData.workLocation.state || loadingCities}
              >
                <option value="">
                  {loadingCities ? 'Loading cities...' : 'Select city'}
                </option>
                {cities.map(city => (
                  <option key={city.value} value={city.value}>{city.label}</option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div>
            <label htmlFor="workLocation.country" className="block text-sm font-medium text-gray-700 mb-1">
              Country *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MapPin className="h-5 w-5 text-gray-400" />
              </div>
              <select
                name="workLocation.country"
                value={localData.workLocation.country}
                onChange={handleChange}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              >
                <option value="">Select country</option>
                {DROPDOWN_OPTIONS.nriCountries.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div>
          <label htmlFor="workLocation.address" className="block text-sm font-medium text-gray-700 mb-1">
            Specific Address
          </label>
          <textarea
            name="workLocation.address"
            value={localData.workLocation.address}
            onChange={handleChange}
            rows={2}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="Enter specific work location address (optional)"
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileDetailsStep;